import { useCallback, useEffect, useState } from "react";
import { fetchAccessRequestCount } from "@/lib/api";
import { useLoadedPermission } from "@/hooks/use-permission";

/** Fired by the access-requests page after an approve or deny, so the nav badge re-reads the count. */
export const ACCESS_REQUESTS_CHANGED_EVENT = "alloy:access-requests-changed";

export function notifyAccessRequestsChanged(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(ACCESS_REQUESTS_CHANGED_EVENT));
}

/**
 * The open access-request count for the Admin nav badge (specs/access-requests S-6, S-13).
 *
 * Fails closed: the count is requested only once the real effective-access contract has loaded
 * and grants admin:users:read (`auth.admin_users.read`), so the badge never issues a call a
 * session cannot make. Any failure reads as 0, which hides the badge; the page itself is where a
 * failure is reported.
 */
export function useAccessRequestOpenCount(): number {
  const { allowed } = useLoadedPermission("user:read");
  const [count, setCount] = useState(0);

  const refresh = useCallback(() => {
    if (!allowed) {
      setCount(0);
      return () => undefined;
    }
    let cancelled = false;
    void fetchAccessRequestCount("OPEN").then((value) => {
      if (!cancelled) setCount(value ?? 0);
    });
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  useEffect(() => refresh(), [refresh]);

  useEffect(() => {
    if (!allowed || typeof window === "undefined") return undefined;
    const onChanged = () => {
      refresh();
    };
    window.addEventListener(ACCESS_REQUESTS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(ACCESS_REQUESTS_CHANGED_EVENT, onChanged);
  }, [allowed, refresh]);

  return count;
}
